import { useCallback, useEffect, useState } from "react";
import { Coins, TrendingDown, Lock, Wallet } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { api, fmtDateTime } from "@/lib/api";
import { Avatar, EmptyState, PageHeader, PageLoader, Pagination, Panel, SearchInput, StatCard, StatusBadge } from "@/components/common";
import { CreditDialog } from "./AdminDialogs";

function Balances({ onChanged }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [credit, setCredit] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users", { params: { role: "student", q, page, limit: 10, status: "approved" } });
    setData(data);
  }, [q, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <Panel>
      <div className="border-b border-slate-100 p-4"><SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search students" testid="credits-student-search" /></div>
      {!data ? <PageLoader /> : (
        <div className="overflow-x-auto">
          <Table data-testid="credit-balances-table">
            <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Available</TableHead><TableHead>Reserved</TableHead><TableHead>Used</TableHead><TableHead>Total allocated</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {data.items.map((u) => (
                <TableRow key={u.id}>
                  <TableCell><div className="flex items-center gap-3"><Avatar src={u.profile_picture} name={u.name} size="h-8 w-8" /><div><p className="font-semibold">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></div></div></TableCell>
                  <TableCell className="font-bold text-[#2DA638]" data-testid={`balance-available-${u.id}`}>{u.wallet.available}</TableCell>
                  <TableCell>{u.wallet.reserved}</TableCell><TableCell>{u.wallet.used}</TableCell><TableCell>{u.wallet.total_allocated ?? 0}</TableCell>
                  <TableCell><Button size="sm" className="rounded-full bg-[#3CC34A] text-[#0B1A33] hover:bg-[#2DA638]" onClick={() => setCredit(u)} data-testid={`manage-credits-${u.id}`}>Manage</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination page={data.page} pages={data.pages} total={data.total} onPage={setPage} />
        </div>
      )}
      <CreditDialog student={credit} onClose={() => setCredit(null)} onSaved={() => { load(); onChanged(); }} />
    </Panel>
  );
}

export function TxTable({ admin = true, items }) {
  return (
    <Table data-testid="transactions-table">
      <TableHeader><TableRow>
        {admin && <TableHead>Student</TableHead>}<TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Balance after</TableHead>
        <TableHead>Reason</TableHead><TableHead>By</TableHead><TableHead>Date</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {items.map((t) => (
          <TableRow key={t.id} data-testid={`tx-row-${t.id}`}>
            {admin && <TableCell className="font-semibold">{t.student_name}</TableCell>}
            <TableCell><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider">{t.type}</span></TableCell>
            <TableCell className={`font-bold ${t.amount >= 0 ? "text-[#2DA638]" : "text-red-500"}`}>{t.amount > 0 ? "+" : ""}{t.amount}</TableCell>
            <TableCell>{t.balance_after}</TableCell>
            <TableCell className="max-w-[220px] truncate text-sm text-slate-600">{t.reason}</TableCell>
            <TableCell className="text-sm">{t.actor_name}</TableCell>
            <TableCell className="whitespace-nowrap text-sm text-slate-500">{fmtDateTime(t.created_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Transactions({ tick }) {
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => {
    const t = setTimeout(async () => {
      const { data } = await api.get("/admin/credits/transactions", { params: { q, page, type: type === "all" ? "" : type } });
      setData(data);
    }, 250);
    return () => clearTimeout(t);
  }, [q, page, type, tick]);
  return (
    <Panel>
      <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row">
        <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search by student" testid="tx-search-input" />
        <Select value={type} onValueChange={(v) => { setType(v); setPage(1); }}>
          <SelectTrigger className="h-10 rounded-full sm:w-48" data-testid="tx-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{["all", "add", "adjust", "reserve", "release", "deduct"].map((o) => <SelectItem key={o} value={o}>{o === "all" ? "All types" : o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {!data ? <PageLoader /> : data.items.length === 0 ? <div className="p-6"><EmptyState title="No transactions" /></div> : (
        <div className="overflow-x-auto"><TxTable items={data.items} /><Pagination page={data.page} pages={data.pages} total={data.total} onPage={setPage} /></div>
      )}
    </Panel>
  );
}

export function CreditsPage() {
  const [s, setS] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, [tick]);
  return (
    <div>
      <PageHeader title="Credit Management" subtitle="Allocate, adjust and audit student credits. 100 credits per completed session." />
      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Allocated" value={s?.credits_allocated} icon={Coins} tone="navy" testid="credits-total-allocated" />
        <StatCard label="Total Consumed" value={s?.credits_consumed} icon={TrendingDown} tone="green" testid="credits-total-consumed" />
        <StatCard label="Currently Reserved" value={s?.credits_reserved} icon={Lock} tone="amber" testid="credits-total-reserved" />
        <StatCard label="Available in Wallets" value={s?.credits_available} icon={Wallet} testid="credits-total-available" />
      </div>
      <Tabs defaultValue="balances">
        <TabsList className="mb-4"><TabsTrigger value="balances" data-testid="credits-tab-balances">Student balances</TabsTrigger>
          <TabsTrigger value="history" data-testid="credits-tab-history">Transaction history</TabsTrigger></TabsList>
        <TabsContent value="balances"><Balances onChanged={() => setTick((x) => x + 1)} /></TabsContent>
        <TabsContent value="history"><Transactions tick={tick} /></TabsContent>
      </Tabs>
    </div>
  );
}

function LogTable({ url, cols, testid }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => { api.get(url, { params: { page } }).then((r) => setData(r.data)); }, [url, page]);
  if (!data) return <PageLoader />;
  if (!data.items.length) return <div className="p-6"><EmptyState title="Nothing logged yet" /></div>;
  return (
    <div className="overflow-x-auto">
      <Table data-testid={testid}>
        <TableHeader><TableRow>{cols.map(([k, l]) => <TableHead key={k}>{l}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{data.items.map((r) => (
          <TableRow key={r.id}>{cols.map(([k, , fmt]) => <TableCell key={k} className="text-sm">{fmt ? fmt(r[k], r) : r[k]}</TableCell>)}</TableRow>
        ))}</TableBody>
      </Table>
      <Pagination page={data.page} pages={data.pages} total={data.total} onPage={setPage} />
    </div>
  );
}

export function AdminSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const Item = ({ label, value, ok, testid }) => (
    <div className="rounded-xl border border-slate-200/80 p-5" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-2 font-semibold capitalize text-[#0B1A33]">{value}</p>
      <span className={`mt-2 inline-block text-xs font-semibold ${ok ? "text-[#2DA638]" : "text-amber-600"}`}>{ok ? "Configured" : "Not configured"}</span>
    </div>
  );
  return (
    <div>
      <PageHeader title="Settings" subtitle="Integrations, notifications and audit logs." />
      {s && (
        <div className="mb-8 grid gap-4 md:grid-cols-4">
          <Item label="Mode" value={s.demo_mode ? "Demo mode" : "Production"} ok={!s.demo_mode} testid="settings-mode" />
          <Item label="Email provider" value={s.email.provider} ok={s.email.configured} testid="settings-email" />
          <Item label="Video provider" value={s.video.provider === "jitsi" ? "Jitsi Meet (free)" : "Zoom"} ok testid="settings-video" />
          <Item label="Zoom API" value={s.video.zoom_configured ? "Credentials set" : "Add ZOOM_* env vars"} ok={s.video.zoom_configured} testid="settings-zoom" />
        </div>
      )}
      <Tabs defaultValue="emails">
        <TabsList className="mb-4"><TabsTrigger value="emails" data-testid="settings-tab-emails">Email notifications</TabsTrigger>
          <TabsTrigger value="activity" data-testid="settings-tab-activity">Admin activity</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security">Security</TabsTrigger></TabsList>
        <TabsContent value="emails"><Panel><LogTable url="/admin/emails" testid="email-log-table" cols={[["to", "Recipient"], ["kind", "Type"], ["subject", "Subject"],
          ["status", "Status", (v, r) => <span title={r.error || ""}><StatusBadge status={v === "skipped_demo" ? "skipped demo" : v} /></span>], ["created_at", "Date", fmtDateTime]]} /></Panel></TabsContent>
        <TabsContent value="activity"><Panel><LogTable url="/admin/activity" testid="activity-log-table" cols={[["admin_name", "Admin"], ["action", "Action"],
          ["details", "Details", (v) => <span className="font-mono text-xs">{JSON.stringify(v)}</span>], ["created_at", "Date", fmtDateTime]]} /></Panel></TabsContent>
        <TabsContent value="security"><Panel className="p-6"><p className="mb-4 text-sm text-slate-600">Change the admin account password.</p>
          <Button asChild className="rounded-full bg-[#1F6FFF]"><a href="/admin/change-password" data-testid="settings-change-password">Change password</a></Button></Panel></TabsContent>
      </Tabs>
    </div>
  );
}
